export const append = <T>(list: Array<T> | undefined, item: T): Array<T> => [
  ...(list || []),
  item,
];

/** Spreads an array to the end of another array */
export const appendArray = <T>(list1: Array<T>, list2: Array<T>): Array<T> => [
  ...list1,
  ...list2,
];

export const getAverage = (arr: number[]): number =>
  arr.reduce((a, b) => a + b, 0) / arr.length;

export const removeFromList = <T>(list: Array<T>, item: T): Array<T> =>
  list.filter((i) => i !== item);

export function getUnique<T>(items: T[], fn?: (item: T) => any): T[] {
  if (!fn) return Array.from(new Set(items));

  const indexes: any = {};

  return items.reduce((acc: T[], curr) => {
    if (indexes[fn(curr)]) {
      return acc;
    }

    indexes[fn(curr)] = true;

    return [...acc, curr];
  }, []);
}

export const excludeNullOrUndefined = <T>(
  list?: (T | null | undefined)[] | null
): T[] => list?.filter(notNullOrUndefined) || [];

export const filterHasValue = excludeNullOrUndefined;

export function notNullOrUndefined<TValue>(
  value: TValue | null | undefined
): value is TValue {
  if (value === null || value === undefined) return false;
  return true;
}

export function notNull<TValue>(
  value: TValue | null | undefined
): TValue | undefined {
  if (value === null) return undefined;
  return value;
}

export const toggleInList = <T>(list?: T[], item?: T): T[] => {
  if (item === undefined) return (list || []) as T[];

  if (!list) {
    return [item];
  }

  if (list.length === 0) {
    return [item];
  }

  return list.includes(item) ? removeFromList(list, item) : append(list, item);
};

export const isInListBy =
  <T>(attr: keyof T) =>
  (list?: T[], item?: T): boolean => {
    return !!item && !!list?.map((i) => i[attr]).includes(item[attr]);
  };

export const toggleInListBy =
  <T>(attr: keyof T) =>
  (list?: T[], item?: T): T[] => {
    if (!item) return (list || []) as T[];

    if (!list) {
      return [item];
    }

    if (list.length === 0) {
      return [item];
    }

    return isInListBy(attr)(list, item)
      ? list.filter((i) => i[attr] !== item[attr])
      : append(list, item);
  };

/** Arranges an array into chunks of a given size */
export const arrayIntoChunks = <T>(list: T[], chunkSize = 10): T[][] => {
  let i = 0;
  let j = list.length;
  const chunkedList: T[][] = [];

  for (i = 0, j = list.length; i < j; i += chunkSize) {
    chunkedList.push(list.slice(i, i + chunkSize));
  }

  return chunkedList;
};

// Takes a list, a function and an item
// Finds matching item in the list and updates it to the item specified
export const findAndUpdate =
  <T>(list: T[]) =>
  (identifier: (item: T) => boolean) =>
  (updatedItem: T): T[] => {
    // Copy the list
    const listCopy = [...list];
    // Locate the index
    const idx = listCopy.findIndex(identifier);
    // If not found, just return original
    if (idx < 0) return list;
    // Update item
    listCopy[idx] = updatedItem;

    return listCopy;
  };

export const itemsInBothArrays = <T>(array1: T[], array2: T[]) =>
  array1.filter((value) => array2.includes(value));

export const anyItemsInBothArrays = <T>(array1: T[], array2: T[]) =>
  array1.some((value) => array2.includes(value));

export const removeFromListByIndex = <T>(list: T[], idx: number): T[] => {
  const updatedList = [...list];
  updatedList.splice(idx, 1);
  return updatedList;
};

export const removeByIndex = removeFromListByIndex;

export const setInListByIndex = <T>(list: T[], idx: number, item: T): T[] => {
  const updatedList = [...list];
  updatedList[idx] = item;
  return updatedList;
};

export const setByIndex = <T>(
  list: T[] | undefined,
  idx: number,
  item: T
): T[] => {
  if (!list) return [item];

  const updatedList = [...list];
  updatedList[idx] = item;
  return updatedList;
};

export const moveBackInListByIndex = <T>(list: T[], idx: number): T[] => {
  if (idx === 0) {
    return list;
  }

  const updatedList = [...list];
  const b = updatedList[idx];
  updatedList[idx] = updatedList[idx - 1];
  updatedList[idx - 1] = b;

  return updatedList;
};

export const moveForwardInListByIndex = <T>(list: T[], idx: number): T[] => {
  if (idx === list.length - 1) {
    return list;
  }

  const updatedList = [...list];
  const b = updatedList[idx];
  updatedList[idx] = updatedList[idx + 1];
  updatedList[idx + 1] = b;

  return updatedList;
};

export const getRandomIndexFromList = <T>(list: T[]) =>
  Math.floor(Math.random() * list.length);

export const getRandomItemFromList = <T>(list: T[]) =>
  list[Math.floor(Math.random() * list.length)];

export const shuffle = <T>(list: T[]): T[] => {
  let currentIndex = list.length,
    randomIndex;

  // While there remain elements to shuffle.
  while (currentIndex != 0) {
    // Pick a remaining element.
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;
    [list[currentIndex], list[randomIndex]] = [
      list[randomIndex],
      list[currentIndex],
    ];
  }

  return list;
};

export const batchArray = <T>(list: T[], batch_size: number): T[][] => {
  const batches: T[][] = [];

  for (let i = 0; i < list.length; i += batch_size) {
    batches.push(list.slice(i, i + batch_size));
  }

  return batches;
};

/** For using in Pipe functions */
export function map<T, R>(fn: (value: T) => R): (arr: T[]) => R[] {
  return (arr: T[]) => arr.map(fn);
}

export const getFirst = <T>(items: T[]) => items[0];

export const hasItems = <T>(items: T[] | undefined): items is T[] =>
  !!items?.length;

export const getLastIndex = <T>(items: T[]) => items.length - 1;

export function getLast10Records<T>(array: T[]): T[] {
  const startIndex = Math.max(array.length - 10, 0);
  return array.slice(startIndex);
}
